import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { AxiosError } from "axios";

import { useApproveTimesheet } from "@/features/timesheets/model/useTimesheets";
import type { Timesheet } from "@/features/timesheets/types";
import { formatIsoTime12h, formatTimeRange12h } from "@/shared/lib/time";
import { Button, Card } from "@/shared/ui";
import { TimesheetFlagBadges, TimesheetStatusBadge } from "@/features/timesheets/ui/TimesheetStatusBadge";

interface Props {
  timesheet: Timesheet;
  workerName?: string;
  shiftRole?: string;
  shiftDate?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  selected?: boolean;
  onSelectChange?: (selected: boolean) => void;
  onDispute: (timesheet: Timesheet) => void;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function TimesheetReviewRow({
  timesheet,
  workerName,
  shiftRole,
  shiftDate,
  scheduledStart,
  scheduledEnd,
  selected,
  onSelectChange,
  onDispute,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const approve = useApproveTimesheet();
  const isPending = timesheet.status === "PENDING_REVIEW";
  const hasFlags =
    timesheet.off_site_checkout ||
    timesheet.no_gps ||
    timesheet.auto_closed ||
    timesheet.manual_by_venue;

  const errMsg = approve.error
    ? approve.error instanceof AxiosError
      ? (approve.error.response?.data?.detail ?? "Could not approve")
      : approve.error.message
    : null;

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        {isPending && onSelectChange && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={(e) => onSelectChange(e.target.checked)}
            disabled={hasFlags}
            title={hasFlags ? "Flagged rows must be reviewed individually" : "Include in bulk approve"}
            className="h-4 w-4 shrink-0 cursor-pointer rounded border-border-strong text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Select for bulk approve"
          />
        )}
        <button
          onClick={() => setExpanded((e) => !e)}
          className="flex flex-1 items-center gap-3 text-left"
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-copy-muted" />
          ) : (
            <ChevronRight className="h-4 w-4 text-copy-muted" />
          )}
          <div className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-1 min-w-0">
            <span className="truncate font-medium text-copy">{workerName ?? "Worker"}</span>
            {shiftRole && <span className="text-sm text-copy-muted">{shiftRole}</span>}
            {shiftDate && <span className="text-sm text-copy-muted">{shiftDate}</span>}
            <span className="ml-auto flex items-center gap-3 text-sm">
              <span className="tabular-nums text-copy">{timesheet.hours_worked}h</span>
              <span className="font-semibold tabular-nums text-copy">${timesheet.gross_pay}</span>
              <TimesheetStatusBadge
                status={timesheet.status}
                reviewedBy={timesheet.reviewed_by}
              />
            </span>
          </div>
        </button>
      </div>

      {expanded && (
        <div className="border-t border-border bg-surface-sunken/40 px-10 py-4 space-y-4">
          {hasFlags && <TimesheetFlagBadges timesheet={timesheet} />}

          <dl className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
            {scheduledStart && scheduledEnd && (
              <div>
                <dt className="text-copy-muted">Scheduled</dt>
                <dd className="text-copy">
                  {formatTimeRange12h(scheduledStart, scheduledEnd)}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-copy-muted">Actual</dt>
              <dd className="text-copy">
                {formatIsoTime12h(timesheet.check_in_at)} – {formatIsoTime12h(timesheet.check_out_at)}
              </dd>
            </div>
            <div>
              <dt className="text-copy-muted">Hours</dt>
              <dd className="font-medium tabular-nums text-copy">{timesheet.hours_worked}h</dd>
            </div>
            <div>
              <dt className="text-copy-muted">Pay at agreed rate</dt>
              <dd className="font-medium tabular-nums text-copy">${timesheet.gross_pay}</dd>
            </div>
          </dl>

          {timesheet.status === "DISPUTED" && (
            <div className="rounded-md border border-destructive bg-destructive-light p-3 text-sm">
              <p className="font-medium text-destructive-foreground">
                Adjusted to {timesheet.adjusted_hours}h (${timesheet.adjusted_pay})
              </p>
              {timesheet.venue_notes && (
                <p className="mt-1 text-destructive-foreground/90">{timesheet.venue_notes}</p>
              )}
            </div>
          )}

          {isPending && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="success"
                size="sm"
                onClick={() => approve.mutate(timesheet.id)}
                disabled={approve.isPending}
              >
                {approve.isPending ? "Approving…" : "Approve"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => onDispute(timesheet)}>
                Dispute
              </Button>
            </div>
          )}

          {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}
        </div>
      )}
    </Card>
  );
}
